import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Format Sri Lankan mobile number to international format (94XXXXXXXXX)
 * Accepts: 0776638308, 776638308, 0777638308, +94776638308, 94776638308
 * Returns: 94776638308 or null if invalid
 */
export const formatSriLankanMobile = (input) => {
  if (!input) return '';
  
  // Remove all non-digit characters except leading +
  let cleaned = input.replace(/\s/g, '');
  
  // Handle +94 format
  if (cleaned.startsWith('+94')) {
    cleaned = cleaned.substring(1); // Remove the +
  }
  
  // If it starts with 0, remove it and add 94
  if (cleaned.startsWith('0')) {
    cleaned = '94' + cleaned.substring(1);
  }
  // If it doesn't start with 94 or 0, assume it's missing country code
  else if (!cleaned.startsWith('94')) {
    cleaned = '94' + cleaned;
  }
  
  return cleaned;
};

/**
 * Validate Sri Lankan mobile number
 * Valid formats: 0776638308, 776638308, 94776638308, +94776638308
 * Must be 10 digits (after 0) or 12 digits (with 94)
 */
export const isValidSriLankanMobile = (input) => {
  if (!input) return false;
  
  // Remove all non-digit characters except leading +
  let cleaned = input.replace(/\s/g, '');
  if (cleaned.startsWith('+')) {
    cleaned = cleaned.substring(1);
  }
  
  // Check if it matches the pattern
  // Should be 12 digits starting with 94, or 10 digits starting with 0, or 9 digits without country code
  const mobileRegex = /^(?:94|0)?7[0-9]{8}$/;
  
  return mobileRegex.test(cleaned);
};

/**
 * Parse Sri Lankan NIC number and extract date of birth and gender.
 * Supports old format: 9 digits + V/X (e.g. 820149894V)
 * Supports new format: 12 digits (e.g. 198201409894)
 * Returns { dateOfBirth: 'YYYY-MM-DD', gender: 'Male'|'Female' } or null if invalid.
 */
export const parseNIC = (nic) => {
  if (!nic) return null;

  const cleaned = nic.trim().toUpperCase();
  let year, dayOfYear;

  const oldNicRegex = /^(\d{9})[VX]$/;
  const newNicRegex = /^(\d{12})$/;

  if (oldNicRegex.test(cleaned)) {
    year = 1900 + parseInt(cleaned.slice(0, 2), 10);
    dayOfYear = parseInt(cleaned.slice(2, 5), 10) - 1; // Old NIC is 1-based; subtract 1 to align with new NIC encoding
  } else if (newNicRegex.test(cleaned)) {
    year = parseInt(cleaned.slice(0, 4), 10);
    dayOfYear = parseInt(cleaned.slice(4, 7), 10);
  } else {
    return null;
  }

  // Determine gender
  const gender = dayOfYear > 500 ? 'Female' : 'Male';
  if (dayOfYear > 500) dayOfYear -= 500;

  // Convert day-of-year to calendar date
  const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const daysInMonth = [31, isLeap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  let month = 0;
  let remaining = dayOfYear;
  for (let i = 0; i < 12; i++) {
    if (remaining <= daysInMonth[i]) {
      month = i + 1;
      break;
    }
    remaining -= daysInMonth[i];
  }

  const day = remaining;
  if (!month || day < 1) return null;

  const dateOfBirth = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return { dateOfBirth, gender };
};

/**
 * Get formatted mobile for display (mask middle digits)
 */
export const getMaskedMobile = (mobile) => {
  if (!mobile) return '';
  const formatted = formatSriLankanMobile(mobile);
  if (formatted.length !== 11) return formatted;
  // Show: 94776***308
  return formatted.slice(0, 5) + '***' + formatted.slice(-3);
};
